import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  pathname?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  keywords?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

const SITE_URL = "https://mcp.starsinger.ai";
const DEFAULT_TITLE = "StarSinger MCP — Let Your AI Assistant Make Songs";
const DEFAULT_DESCRIPTION =
  "StarSinger's MCP server lets Claude, ChatGPT, Cursor and any MCP client write and sing a finished song, make a music video starring you, and search 20,000+ AI songs. Hosted with OAuth, or via npm.";
const DEFAULT_OG_IMAGE = "/og-image.png";

export function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  pathname = "/",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  keywords,
  article,
}: SEOProps) {
  const canonicalUrl = `${SITE_URL}${pathname}`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL - Critical for SEO */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="StarSinger MCP" />

      {/* Article-specific OG tags */}
      {ogType === "article" && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullOgImage} />
    </Helmet>
  );
}
